import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import './Teacher.css';
import Layout from '../../../../components/Layout';
import {
  Autocomplete,
  Box,
  FormControl,
  Input,
  InputAdornment,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Slider,
  Button,
  Checkbox,
  Radio,
  FormControlLabel,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import usePagination from '@mui/material/usePagination';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import TeacherTable from '../../../../components/Admin/UserManagement/Teacher/TeacherTable/TeacherTable';
import { axiosInstance } from '../../../../utils/tokenUtils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Popover from '@mui/material/Popover';
import { toast } from 'react-hot-toast';
import DropdownIcon from '../../../../assets/Admin/Accordion/dropdown-icon.svg';
import ToggleTypesOfSession from '../../../../components/Admin/UserManagement/Teacher/ToggleTypesOfSession/ToggleTypesOfSession';
import { TableSkeleton } from '../../../../components/Admin/SkeletonLoader/SkeletonLoader';

const AcademicLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'elementary', label: 'Elementary' },
  { value: 'high_school', label: 'High School' },
  { value: 'middle_school', label: 'Middle School' },
  { value: 'university', label: 'University' },
];

const CreativeLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'professional', label: 'Professional' },
];

const CategoryArray = [
  { value: 'All', label: 'All' },
  { value: 'teacher', label: 'Instructors' },
  { value: 'student_as_a_teacher', label: 'Learner As Instructor' },
];

const StatusArray = [
  { value: 'All', label: 'All' },
  { value: 'true', label: 'Enable' },
  { value: 'false', label: 'Disable' },
];

const GenderArray = [
  { value: 'All', label: 'All' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

const RatingArray = [
  { value: 'All', label: 'All' },
  { value: '5', label: '5 & below' },
  { value: '4', label: '4 & below' },
  { value: '3', label: '3 & below' },
  { value: '2', label: '2 & below' },
];

const SessionTypeArray = [
  { value: 'All', label: 'All' },
  { value: 'one_on_one', label: 'One on One Session' },
  { value: 'group', label: 'Group Session' },
  { value: 'prime', label: 'Prime Session' },
];

// Generate experience array (1-10 + More than 10)
const ExperienceArray = [
  { value: 'All', label: 'All' },
  { value: '1-3', label: '1-3 years' },
  { value: '4-6', label: '4-6 years' },
  { value: '7-9', label: '7-9 years' },
  { value: '10+', label: '10+ years' },
];

const TAGS_PAGE_SIZE = 40;

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3, pb: 0 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// Utility function to format text
const formatText = text => {
  if (!text) return text;
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Teacher = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [creativeArtsTeacherData, setCreativeArtsTeacherData] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);

  // Active filter states
  const [levelToTeach, setLevelToTeach] = useState('All');
  const [category, setCategory] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [experience, setExperience] = useState('All');
  const [language, setLanguage] = useState('All');
  const [city, setCity] = useState('All');
  const [country, setCountry] = useState('All');
  const [gender, setGender] = useState('All');
  const [revenue, setRevenue] = useState([0, 10000]);
  const [ratingFilter, setRatingFilter] = useState('All');
  const [couponCode, setCouponCode] = useState('All');
  const [sessionType, setSessionType] = useState('All');
  const [totalSpent, setTotalSpent] = useState([0, 10000]);
  const [totalSessions, setTotalSessions] = useState([0, 1000]);

  // Pending filter states - will be applied when user clicks Apply
  const [pendingLevelToTeach, setPendingLevelToTeach] = useState('All');
  const [pendingCategory, setPendingCategory] = useState('All');
  const [pendingStatusFilter, setPendingStatusFilter] = useState('All');
  const [pendingSelectedSubject, setPendingSelectedSubject] = useState('All');
  const [pendingSelectedTags, setPendingSelectedTags] = useState([]);
  const [pendingExperience, setPendingExperience] = useState('All');
  const [pendingLanguage, setPendingLanguage] = useState('All');
  const [pendingCity, setPendingCity] = useState('All');
  const [pendingCountry, setPendingCountry] = useState('All');
  const [pendingGender, setPendingGender] = useState('All');
  const [pendingRevenue, setPendingRevenue] = useState([0, 10000]);
  const [pendingRatingFilter, setPendingRatingFilter] = useState('All');
  const [pendingCouponCode, setPendingCouponCode] = useState('All');
  const [pendingSessionType, setPendingSessionType] = useState('All');
  const [pendingTotalSpent, setPendingTotalSpent] = useState([0, 10000]);
  const [pendingTotalSessions, setPendingTotalSessions] = useState([0, 1000]);

  // Data states
  const [academicTeacherData, setAcademicTeacherData] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subjectList, setSubjectList] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [tagIdToTitleMap, setTagIdToTitleMap] = useState({});
  const [languageList, setLanguageList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [couponList, setCouponList] = useState([]);

  // Sort states
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Cache flags to avoid refetching supporting data
  const [subjectsLoadedForAcademic, setSubjectsLoadedForAcademic] = useState(false);
  const [subjectsLoadedForCreative, setSubjectsLoadedForCreative] = useState(false);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);
  const [countriesLoaded, setCountriesLoaded] = useState(false);
  const [couponsLoaded, setCouponsLoaded] = useState(false);

  // Coupon loading states
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponOffset, setCouponOffset] = useState(0);
  const [hasMoreCoupons, setHasMoreCoupons] = useState(true);

  // Whether the current admin can view coupons (gates the Coupon Code filter).
  // Roles without `view_coupons` (e.g. Talent Manager) would otherwise hit a
  // 403 on /admin/coupons and trigger a "no permission" toast on filter open.
  const canViewCoupons = useMemo(() => {
    try {
      const me = JSON.parse(sessionStorage.getItem('rbac_admin_me') || '{}');
      const roles = Array.isArray(me.roles) ? me.roles : [];
      const permissions = Array.isArray(me.permissions) ? me.permissions : [];
      return (
        roles.includes('super_admin') ||
        roles.includes('admin') ||
        permissions.includes('view_coupons')
      );
    } catch {
      return false;
    }
  }, []);

  // Search states for dropdowns
  const [languageSearch, setLanguageSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  // Tag dropdown state
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagAnchorEl, setTagAnchorEl] = useState(null);

  // Pending Tag pagination/loading state (used inside filter dropdown)
  const [pendingTagList, setPendingTagList] = useState([]);
  const [pendingTagLoading, setPendingTagLoading] = useState(false);
  const [pendingTagOffset, setPendingTagOffset] = useState(0);
  const [hasMorePendingTags, setHasMorePendingTags] = useState(true);

  const modalRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const hasReceivedDataRef = useRef(false);

  // Reset hasReceivedDataRef when navigating to this page
  useEffect(() => {
    hasReceivedDataRef.current = false;
    setLoading(true);
  }, [location.pathname]);

  // Debounced search values
  const debouncedLanguageSearch = useDebounce(languageSearch, 300);
  const debouncedCountrySearch = useDebounce(countrySearch, 300);
  const debouncedCitySearch = useDebounce(citySearch, 300);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Create refs to access current values without triggering re-renders
  const subjectListRef = useRef([]);
  const tagListRef = useRef([]);
  const tagIdToTitleMapRef = useRef({});
  const countryListRef = useRef([]);
  const cityListRef = useRef([]);

  // Update refs when state changes
  useEffect(() => {
    subjectListRef.current = subjectList;
  }, [subjectList]);
  useEffect(() => {
    tagListRef.current = tagList;
  }, [tagList]);
  useEffect(() => {
    tagIdToTitleMapRef.current = tagIdToTitleMap;
  }, [tagIdToTitleMap]);
  useEffect(() => {
    countryListRef.current = countryList;
  }, [countryList]);
  useEffect(() => {
    cityListRef.current = cityList;
  }, [cityList]);

  // Remove selectionMode state - checkboxes will always be visible
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [sessionToToggle, setSessionToToggle] = useState('');
  const [isEnabling, setIsEnabling] = useState(false);

  // Selection handlers used by tables
  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAllVisible = (ids, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  // Pagination states
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  // Force refresh index to re-fetch without changing page
  const [dataRefreshIndex, setDataRefreshIndex] = useState(0);

  // Consolidated, cancellable effect to fetch teacher data
  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      try {
        setLoading(true);
        hasReceivedDataRef.current = false;
        const params = new URLSearchParams();
        params.append('approval_status', 'approved');
        params.append('teacher_type', tabValue === 0 ? 'academic' : 'creative');

        // Pagination
        params.append('page', page.toString());
        params.append('page_size', rowsPerPage.toString());

        // Filters
        if (debouncedSearchTerm) params.append('query', debouncedSearchTerm);
        if (levelToTeach !== 'All') params.append('level_to_teach', levelToTeach);
        if (category !== 'All') params.append('category', category);
        if (statusFilter !== 'All') params.append('is_active', statusFilter);
        if (selectedSubject !== 'All') {
          params.append('subject_name', selectedSubject);
        }

        if (selectedTags.length > 0) {
          const tagNames = selectedTags
            .map(tagId => tagIdToTitleMapRef.current[tagId])
            .filter(Boolean);
          if (tagNames.length > 0) params.append('tag_names', tagNames.join(','));
        }
        if (experience !== 'All') params.append('years_of_experience', experience);
        if (language !== 'All') params.append('teaching_language', language);
        if (country !== 'All') {
          const countryObj = countryListRef.current.find(c => c.name === country);
          if (countryObj) params.append('country', countryObj.name);
        }
        if (city !== 'All') {
          const cityObj = cityListRef.current.find(c => c.name === city);
          if (cityObj) params.append('city', cityObj.name);
        }
        if (gender !== 'All') params.append('gender', gender);
        if (ratingFilter !== 'All') params.append('rating', ratingFilter);
        if (sessionType !== 'All') params.append('session_type', sessionType);
        if (totalSpent[0] > 0 || totalSpent[1] < 10000) {
          params.append('min_spent', `${totalSpent[0]}`);
          params.append('max_spent', `${totalSpent[1]}`);
        }
        if (totalSessions[0] > 0 || totalSessions[1] < 1000) {
          params.append('min_sessions', `${totalSessions[0]}`);
          params.append('max_sessions', `${totalSessions[1]}`);
        }
        if (couponCode !== 'All') params.append('coupon_code', couponCode);

        // Sorting
        if (sortBy) {
          const sortParam = `${sortOrder === 'desc' ? '-' : ''}${sortBy}`;
          params.append('sort_by', sortParam);
        }

        const { data } = await axiosInstance.get(`/admin/teachers?${params.toString()}`, {
          signal: controller.signal,
        });

        setTotalCount(data?.count || 0);
        if (tabValue === 0) {
          setAcademicTeacherData(data?.results || []);
        } else {
          setCreativeArtsTeacherData(data?.results || []);
        }
        hasReceivedDataRef.current = true;
      } catch (error) {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
        console.log('Error fetching teacher data:', error);
        if (tabValue === 0) setAcademicTeacherData([]);
        else setCreativeArtsTeacherData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [
    tabValue,
    debouncedSearchTerm,
    levelToTeach,
    category,
    statusFilter,
    selectedSubject,
    selectedTags,
    experience,
    language,
    country,
    city,
    gender,
    ratingFilter,
    sessionType,
    totalSpent,
    totalSessions,
    couponCode,
    page,
    rowsPerPage,
    dataRefreshIndex,
    sortBy,
    sortOrder,
  ]);

  // API Functions (supporting data)
  const fetchSubjects = async subjectType => {
    try {
      const { data } = await axiosInstance.get(`/common/subjects?subject_type=${subjectType}`);
      setSubjectList(data.results || []);
    } catch (error) {
      console.log('Error fetching subjects:', error);
      setSubjectList([]);
    }
  };

  // Helper to merge titles into the map
  const mergeTagsIntoMap = tags => {
    setTagIdToTitleMap(prev => {
      const next = { ...prev };
      tags.forEach(t => {
        if (t?.id) next[t.id] = t.title;
      });
      return next;
    });
  };

  const fetchTags = async (subjectId, offset = 0) => {
    try {
      const params = new URLSearchParams();
      params.append('offset', offset.toString());
      params.append('limit', TAGS_PAGE_SIZE.toString());

      const { data } = await axiosInstance.get(`/common/${subjectId}/tags?${params.toString()}`);

      if (offset === 0) {
        // First load - replace the list
        setTagList(data?.results || []);
        mergeTagsIntoMap(data?.results || []);
      } else {
        // Subsequent loads - append to the list
        setTagList(prev => {
          const next = [...prev, ...(data?.results || [])];
          mergeTagsIntoMap(data?.results || []);
          return next;
        });
      }
    } catch (error) {
      console.log('Error fetching tags:', error);
      if (offset === 0) {
        setTagList([]);
      }
    } finally {
    }
  };

  const fetchPendingTags = async (subjectId, offset = 0) => {
    try {
      setPendingTagLoading(true);
      const params = new URLSearchParams();
      params.append('offset', offset.toString());
      params.append('limit', TAGS_PAGE_SIZE.toString());

      const { data } = await axiosInstance.get(`/common/${subjectId}/tags?${params.toString()}`);

      if (offset === 0) {
        setPendingTagList(data?.results || []);
        mergeTagsIntoMap(data?.results || []);
      } else {
        setPendingTagList(prev => {
          const next = [...prev, ...(data?.results || [])];
          mergeTagsIntoMap(data?.results || []);
          return next;
        });
      }

      setPendingTagOffset(offset + TAGS_PAGE_SIZE);
      setHasMorePendingTags((data?.results || []).length === TAGS_PAGE_SIZE);
    } catch (error) {
      console.log('Error fetching pending tags:', error);
      if (offset === 0) setPendingTagList([]);
      setPendingTagOffset(0);
      setHasMorePendingTags(false);
    } finally {
      setPendingTagLoading(false);
    }
  };

  const fetchLanguages = async (searchQuery = '') => {
    try {
      const url = searchQuery ? `/auth/languages?search_query=${searchQuery}` : '/auth/languages';
      const { data } = await axiosInstance.get(url);
      setLanguageList(data || []);
      setLanguagesLoaded(true);
    } catch (error) {
      console.log('Error fetching languages:', error);
      setLanguageList([]);
    }
  };

  const fetchCountries = async (searchQuery = '') => {
    try {
      const url = searchQuery ? `/auth/countries?search_query=${searchQuery}` : '/auth/countries';
      const { data } = await axiosInstance.get(url);
      setCountryList(data || []);
      setCountriesLoaded(true);
    } catch (error) {
      console.log('Error fetching countries:', error);
      setCountryList([]);
    }
  };

  const fetchCities = async (countryId, searchQuery = '') => {
    try {
      const url = searchQuery
        ? `/auth/countries/${countryId}/cities?search_query=${searchQuery}`
        : `/auth/countries/${countryId}/cities`;
      const { data } = await axiosInstance.get(url);
      setCityList(data || []);
    } catch (error) {
      console.log('Error fetching cities:', error);
      setCityList([]);
    }
  };

  const fetchCoupons = async (offset = 0) => {
    try {
      setCouponLoading(true);
      const params = new URLSearchParams();
      params.append('offset', offset.toString());
      params.append('limit', '15');

      const { data } = await axiosInstance.get(
        `/admin/coupons?${params.toString()}&include_deleted=true`
      );

      if (offset === 0) {
        // First load - replace the list
        setCouponList(data?.results || []);
        setCouponsLoaded(true);
      } else {
        // Subsequent loads - append to the list
        setCouponList(prev => [...prev, ...(data?.results || [])]);
      }

      setCouponOffset(offset + 15);
      setHasMoreCoupons((data?.results || []).length === 15);
    } catch (error) {
      console.log('Error fetching coupons:', error);
      if (offset === 0) {
        setCouponList([]);
      }
      setCouponOffset(0);
      setHasMoreCoupons(false);
    } finally {
      setCouponLoading(false);
    }
  };

  const loadMoreCoupons = () => {
    if (!couponLoading && hasMoreCoupons) {
      fetchCoupons(couponOffset);
    }
  };

  // Lazy-load supporting data when filter opens
  const ensureSupportingDataLoaded = useCallback(async () => {
    const subjectType = tabValue === 0 ? 'academic' : 'creative';
    try {
      if (subjectType === 'academic' && !subjectsLoadedForAcademic) {
        await fetchSubjects('academic');
        setSubjectsLoadedForAcademic(true);
      }
      if (subjectType === 'creative' && !subjectsLoadedForCreative) {
        await fetchSubjects('creative');
        setSubjectsLoadedForCreative(true);
      }
      if (!languagesLoaded) await fetchLanguages();
      if (!countriesLoaded) await fetchCountries();
      if (canViewCoupons && !couponsLoaded) await fetchCoupons(0);
    } catch (e) {
      // Logged inside individual fetchers
    }
  }, [
    tabValue,
    subjectsLoadedForAcademic,
    subjectsLoadedForCreative,
    languagesLoaded,
    countriesLoaded,
    couponsLoaded,
    canViewCoupons,
  ]);

  // Add click outside handler for tag dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      // Check if click is outside both the anchor and the popper content
      if (
        tagAnchorEl &&
        !tagAnchorEl.contains(event.target) &&
        !document.querySelector('.tag-dropdown-paper')?.contains(event.target)
      ) {
        setTagDropdownOpen(false);
        setTagAnchorEl(null);
      }
    };

    if (tagDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tagDropdownOpen, tagAnchorEl]);

  // Fetch languages when search changes
  useEffect(() => {
    if (debouncedLanguageSearch !== languageSearch) return;
    if (!languagesLoaded && !openFilter) return; // Only when filter is open or already loaded
    fetchLanguages(debouncedLanguageSearch);
  }, [debouncedLanguageSearch, languagesLoaded, openFilter]);

  // Fetch countries when search changes
  useEffect(() => {
    if (debouncedCountrySearch !== countrySearch) return;
    if (!countriesLoaded && !openFilter) return; // Only when filter is open or already loaded
    fetchCountries(debouncedCountrySearch);
  }, [debouncedCountrySearch, countriesLoaded, openFilter]);

  // Fetch cities when country or city search changes
  useEffect(() => {
    if (country !== 'All') {
      const countryObj = countryList.find(c => c.name === country);
      if (countryObj) {
        fetchCities(countryObj.id, debouncedCitySearch);
      }
    } else {
      setCityList([]);
      setCity('All');
    }
  }, [country, debouncedCitySearch, countryList]);

  // Fetch tags when subject changes
  useEffect(() => {
    if (selectedSubject !== 'All') {
      const subject = subjectList.find(s => s.title === selectedSubject);
      if (subject) {
        fetchTags(subject.id);
      }
    } else {
      setTagList([]);
      setSelectedTags([]);
    }
  }, [selectedSubject, subjectList]);

  // Fetch tags for pending subject when filter is open or pending subject changes
  useEffect(() => {
    if (!openFilter) return;
    if (pendingSelectedSubject !== 'All') {
      const subject = subjectList.find(s => s.title === pendingSelectedSubject);
      if (subject) {
        setPendingTagOffset(0);
        setHasMorePendingTags(true);
        fetchPendingTags(subject.id, 0);
      }
    } else {
      setPendingTagList([]);
    }
  }, [pendingSelectedSubject, openFilter, subjectList]);

  // Initialize pending filters when opening the filter modal
  const initializePendingFilters = () => {
    setPendingLevelToTeach(levelToTeach);
    setPendingCategory(category);
    setPendingStatusFilter(statusFilter);
    setPendingSelectedSubject(selectedSubject);
    setPendingSelectedTags([...selectedTags]);
    setPendingExperience(experience);
    setPendingLanguage(language);
    setPendingCity(city);
    setPendingCountry(country);
    setPendingGender(gender);
    setPendingRevenue([...revenue]);
    setPendingRatingFilter(ratingFilter);
    setPendingCouponCode(couponCode);
    setPendingSessionType(sessionType);
    setPendingTotalSpent([...totalSpent]);
    setPendingTotalSessions([...totalSessions]);
  };

  // Apply pending filters to active filters
  const applyPendingFilters = () => {
    setLevelToTeach(pendingLevelToTeach);
    setCategory(pendingCategory);
    setStatusFilter(pendingStatusFilter);
    setSelectedSubject(pendingSelectedSubject);
    setSelectedTags([...pendingSelectedTags]);
    setExperience(pendingExperience);
    setLanguage(pendingLanguage);
    setCity(pendingCity);
    setCountry(pendingCountry);
    setGender(pendingGender);
    setRevenue([...pendingTotalSpent]);
    setTotalSpent([...pendingTotalSpent]);
    setTotalSessions([...pendingTotalSessions]);
    setRatingFilter(pendingRatingFilter);
    setCouponCode(pendingCouponCode);
    setSessionType(pendingSessionType);

    // Reset pagination when filters are applied
    setPage(1);
  };

  // Event Handlers
  const handleClickToOpenFilter = async event => {
    setAnchorEl(event.currentTarget);
    setOpenFilter(true);
    initializePendingFilters(); // Initialize pending filters with current active filters
    await ensureSupportingDataLoaded(); // Lazy-load once per type
  };

  const handleApplyFilter = () => {
    setOpenFilter(false);
    setAnchorEl(null);
    applyPendingFilters(); // Apply the pending filters
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setOpenFilter(false);
    // Don't apply filters if canceled
  };

  const handleClearAll = () => {
    // Clear pending filter states
    setPendingLevelToTeach('All');
    setPendingCategory('All');
    setPendingStatusFilter('All');
    setPendingSelectedSubject('All');
    setPendingSelectedTags([]);
    setPendingExperience('All');
    setPendingLanguage('All');
    setPendingCity('All');
    setPendingCountry('All');
    setPendingGender('All');
    setPendingRevenue([0, 10000]);
    setPendingRatingFilter('All');
    setPendingCouponCode('All');
    setPendingSessionType('All');
    setLanguageSearch('');
    setCountrySearch('');
    setCitySearch('');
    setPendingTotalSpent([0, 10000]);
    setPendingTotalSessions([0, 1000]);
  };

  const handleChangeSearch = e => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset pagination on new search input
  };

  // Get appropriate level array based on selected tab
  const getLevelArray = () => {
    return tabValue === 0 ? AcademicLevelArray : CreativeLevelArray;
  };

  // Revenue slider handlers
  const handleRevenueChange = (event, newValue) => {
    setPendingRevenue(newValue);
  };

  const valuetext = value => {
    return `$${value}`;
  };

  // New slider handlers for pending ranges
  const handlePendingTotalSpentChange = (event, newValue) => {
    setPendingTotalSpent(newValue);
  };
  const handlePendingTotalSessionsChange = (event, newValue) => {
    setPendingTotalSessions(newValue);
  };

  // Tag handlers
  const handleTagDropdownClick = event => {
    setTagAnchorEl(event.currentTarget);
    setTagDropdownOpen(true);
  };

  const handleTagDropdownClose = () => {
    setTagDropdownOpen(false);
    setTagAnchorEl(null);
  };

  const handleTagSelection = tagId => {
    setPendingSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
    // Don't close the dropdown after selection
  };

  // Infinite scroll handler for tag list
  const handleTagListScroll = e => {
    const target = e.currentTarget;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 20;
    if (!nearBottom || pendingTagLoading || !hasMorePendingTags) return;

    const subjectTitle = openFilter ? pendingSelectedSubject : selectedSubject;
    if (!subjectTitle || subjectTitle === 'All') return;

    const subject = subjectList.find(s => s.title === subjectTitle);
    if (!subject) return;

    fetchPendingTags(subject.id, pendingTagOffset);
  };

  // Get display text for tags
  const getTagDisplayText = () => {
    if (selectedTags.length === 0) return 'All';

    const selectedTagNames = selectedTags
      .map(tagId => {
        const title = tagIdToTitleMap[tagId];
        return title ? formatText(title) : '';
      })
      .filter(Boolean);

    if (selectedTagNames.length <= 2) {
      return selectedTagNames.join(', ');
    } else {
      return `${selectedTagNames.slice(0, 2).join(', ')} +${selectedTagNames.length - 2} more`;
    }
  };

  // Get display text for pending tag selections inside the filter UI
  const getPendingTagDisplayText = () => {
    if (pendingSelectedTags.length === 0) return 'All';

    const selectedTagNames = pendingSelectedTags
      .map(tagId => {
        const title = tagIdToTitleMap[tagId];
        return title ? formatText(title) : '';
      })
      .filter(Boolean);

    if (selectedTagNames.length <= 2) {
      return selectedTagNames.join(', ');
    } else {
      return `${selectedTagNames.slice(0, 2).join(', ')} +${selectedTagNames.length - 2} more`;
    }
  };

  // Function to get active filters
  const getActiveFilters = () => {
    const filters = [];

    if (levelToTeach !== 'All') {
      filters.push({
        type: 'levelToTeach',
        label: `Level: ${formatText(levelToTeach)}`,
        value: levelToTeach,
      });
    }

    if (category !== 'All') {
      filters.push({
        type: 'category',
        label: `Category: ${CategoryArray.find(c => c.value === category)?.label || formatText(category)}`,
        value: category,
      });
    }

    if (statusFilter !== 'All') {
      const statusLabel =
        StatusArray.find(s => s.value === statusFilter)?.label || formatText(statusFilter);
      filters.push({
        type: 'statusFilter',
        label: `Status: ${statusLabel}`,
        value: statusFilter,
      });
    }

    if (selectedSubject !== 'All') {
      filters.push({
        type: 'selectedSubject',
        label: `Subject: ${formatText(selectedSubject)}`,
        value: selectedSubject,
      });
    }

    if (selectedTags.length > 0) {
      const tagNames = selectedTags
        .map(tagId => tagIdToTitleMap[tagId])
        .filter(Boolean)
        .map(name => formatText(name));

      filters.push({
        type: 'selectedTags',
        label: `Tags: ${tagNames.join(', ')}`,
        value: selectedTags,
      });
    }

    if (experience !== 'All') {
      filters.push({
        type: 'experience',
        label: `Experience: ${experience === '11' ? 'More than 10 years' : `${experience} year${parseInt(experience) > 1 ? 's' : ''}`}`,
        value: experience,
      });
    }

    if (language !== 'All') {
      filters.push({
        type: 'language',
        label: `Language: ${language}`,
        value: language,
      });
    }

    if (country !== 'All') {
      filters.push({
        type: 'country',
        label: `Country: ${country}`,
        value: country,
      });
    }

    if (city !== 'All') {
      filters.push({
        type: 'city',
        label: `City: ${city}`,
        value: city,
      });
    }

    if (gender !== 'All') {
      filters.push({
        type: 'gender',
        label: `Gender: ${formatText(gender)}`,
        value: gender,
      });
    }

    if (ratingFilter !== 'All') {
      filters.push({
        type: 'ratingFilter',
        label: `Rating: ${ratingFilter} & below`,
        value: ratingFilter,
      });
    }

    if (sessionType !== 'All') {
      const label =
        SessionTypeArray.find(s => s.value === sessionType)?.label || formatText(sessionType);
      filters.push({
        type: 'sessionType',
        label: `Session Type: ${label}`,
        value: sessionType,
      });
    }

    if (totalSpent[0] > 0 || totalSpent[1] < 10000) {
      filters.push({
        type: 'totalSpent',
        label: `Total Spent: $${totalSpent[0]} - $${totalSpent[1]}`,
        value: totalSpent,
      });
    }

    if (totalSessions[0] > 0 || totalSessions[1] < 1000) {
      filters.push({
        type: 'totalSessions',
        label: `Total Sessions: ${totalSessions[0]} - ${totalSessions[1]}`,
        value: totalSessions,
      });
    }

    if (couponCode !== 'All') {
      filters.push({
        type: 'couponCode',
        label: `Coupon Code: ${couponCode}`,
        value: couponCode,
      });
    }

    return filters;
  };

  // Handler for removing a filter
  const handleRemoveFilter = filterType => {
    switch (filterType) {
      case 'levelToTeach':
        setLevelToTeach('All');
        break;
      case 'category':
        setCategory('All');
        break;
      case 'statusFilter':
        setStatusFilter('All');
        break;
      case 'selectedSubject':
        setSelectedSubject('All');
        setSelectedTags([]); // Reset tags when subject changes
        break;
      case 'selectedTags':
        setSelectedTags([]);
        break;
      case 'experience':
        setExperience('All');
        break;
      case 'language':
        setLanguage('All');
        break;
      case 'country':
        setCountry('All');
        setCity('All'); // Reset city when country changes
        break;
      case 'city':
        setCity('All');
        break;
      case 'gender':
        setGender('All');
        break;
      case 'ratingFilter':
        setRatingFilter('All');
        break;
      case 'sessionType':
        setSessionType('All');
        break;
      case 'totalSpent':
        setTotalSpent([0, 10000]);
        setRevenue([0, 10000]);
        break;
      case 'totalSessions':
        setTotalSessions([0, 1000]);
        break;
      case 'couponCode':
        setCouponCode('All');
        break;
      default:
        break;
    }

    // Reset pagination when filters are removed
    setPage(1);
  };

  // Replace the existing handleSessionTypeChange function with this updated version
  const handleSessionTypeChange = sessionType => {
    setSessionToToggle(sessionType);
    setConfirmModalOpen(true);
  };

  // Update handleConfirmPermissionChange to handle additional date parameters and make the API call
  const handleConfirmPermissionChange = async data => {
    try {
      const teacherIds = Array.from(selectedIds);

      // Build permissions object - only include the permission being changed
      const permissions = {};

      if (data.sessionType === 'group') {
        // Only update group session permission
        permissions.allow_group_session = data.isEnabling;
      } else if (data.sessionType === 'prime') {
        // Only update prime session permission
        permissions.allow_prime_session = data.isEnabling;

        // For prime sessions, also add date parameters if we're enabling
        if (data.isEnabling) {
          permissions.prime_session_start_date = data.startDate;
          permissions.prime_session_end_date = data.endDate;
        }
      }

      // Make API call
      await axiosInstance.post('/admin/teachers/bulk-permissions', {
        teacher_ids: teacherIds,
        permissions,
      });

      // Optimistically update current table data so UI reflects changes immediately
      const selectedIdSet = new Set(teacherIds);
      const applyLocalUpdate = row => {
        if (!selectedIdSet.has(row.id)) return row;
        if (data.sessionType === 'group') {
          return {
            ...row,
            allow_group_session: data.isEnabling,
          };
        }
        if (data.sessionType === 'prime') {
          return {
            ...row,
            allow_prime_session: data.isEnabling,
            prime_session_start_date: data.isEnabling
              ? data.startDate
              : row.prime_session_start_date,
            prime_session_end_date: data.isEnabling ? data.endDate : row.prime_session_end_date,
          };
        }
        return row;
      };

      if (tabValue === 0) {
        setAcademicTeacherData(prev => prev.map(applyLocalUpdate));
      } else {
        setCreativeArtsTeacherData(prev => prev.map(applyLocalUpdate));
      }

      // Clear selection after successful update
      setSelectedIds(new Set());
      setConfirmModalOpen(false);

      // Force a data refresh without changing pagination
      setDataRefreshIndex(prev => prev + 1);

      // Show success message
      const actionText = data.isEnabling ? 'enabled' : 'disabled';
      toast.success(
        `Successfully ${actionText} ${data.sessionType} sessions for ${teacherIds.length} instructor(s)`
      );
    } catch (error) {
      console.error('Error updating permissions:', error);
      const apiMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        'Failed to update permissions';
      toast.error(apiMessage);
    }
  };

  // Get filtered data for display
  const getFilteredData = useCallback(() => {
    const baseData = tabValue === 0 ? academicTeacherData : creativeArtsTeacherData;
    return baseData;
  }, [tabValue, academicTeacherData, creativeArtsTeacherData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset some filters on tab switch
    setLevelToTeach('All');
    setSelectedSubject('All');
    setSelectedTags([]);
    setCouponCode('All');
    setPage(1);
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
    setPage(1); // Reset to first page when changing page size
  };

  // Pagination hook
  const { items } = usePagination({
    count: Math.ceil(totalCount / rowsPerPage),
    page: page,
    onChange: handlePageChange,
  });

  return (
    <Layout title="Klass Online | Instructors">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            <Box className="flex justify-between items-center">
              <Typography className="teacher-title">Instructors</Typography>
              <Box className="flex items-center gap-4 w-[40%]">
                <Input
                  sx={{
                    textDecoration: 'none',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                    background: '#FFF',
                    width: '100%',
                    height: '40px',
                  }}
                  type="search"
                  placeholder="Search"
                  aria-label="Search"
                  value={searchTerm}
                  onChange={handleChangeSearch}
                  startAdornment={<SearchIcon className="search-icon-css" />}
                />
                {openFilter ? (
                  <img
                    src={ColoredFilterIcon}
                    alt="Filter-icon"
                    style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                    onClick={handleClickToOpenFilter}
                  />
                ) : (
                  <img
                    src={FilterIcon}
                    alt="Filter-icon"
                    style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                    onClick={handleClickToOpenFilter}
                  />
                )}
              </Box>
            </Box>

            {/* Replace the bulk actions buttons with new dropdown */}
            {selectedIds.size > 0 && (
              <Box className="bulk-actions-container">
                <Box className="bulk-actions-left">
                  <Typography className="bulk-actions-count">
                    {selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} selected
                  </Typography>
                </Box>
                <Box className="bulk-actions-right">
                  <FormControl sx={{ minWidth: 200 }}>
                    <Select
                      value=""
                      displayEmpty
                      onChange={e => handleSessionTypeChange(e.target.value)}
                      className="session-type-select"
                      IconComponent={() => (
                        <img src={DropdownIcon} alt="dropdown" style={{ marginRight: '10px' }} />
                      )}
                    >
                      <MenuItem value="" disabled>
                        Select Session Type
                      </MenuItem>
                      <MenuItem value="group">Group Session</MenuItem>
                      <MenuItem value="prime">Prime Session</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setSelectedIds(new Set())}
                    className="bulk-action-button cancel-button"
                  >
                    <ClearIcon className="bulk-action-icon" />
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {/* Replace the Dialog component with our custom component */}
            <ToggleTypesOfSession
              open={confirmModalOpen}
              onClose={() => setConfirmModalOpen(false)}
              sessionType={sessionToToggle}
              selectedIds={selectedIds}
              isEnabling={true}
              onConfirm={handleConfirmPermissionChange}
            />

            {/* Filter Modal */}
            <Popover
              open={openFilter}
              anchorEl={anchorEl}
              onClose={handleCloseFilter}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                className: 'filter-modal-container',
              }}
            >
              {/* Header */}
              <Box className="filter-modal-header">
                <Typography className="filter-modal-title">Filters</Typography>
              </Box>

              {/* Scrollable Content */}
              <Box className="filter-modal-content">
                {/* Grid for filter items */}
                <Box className="filter-grid">
                  {/* Level to Teach Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Level to Teach</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={pendingLevelToTeach}
                        onChange={e => setPendingLevelToTeach(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {getLevelArray().map(level => (
                          <MenuItem
                            key={level.value}
                            value={level.value}
                            className="dropdown-option"
                          >
                            {level.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Category Filter - Only for Academic */}
                  {/* {tabValue === 0 && (
                    <Box className="filter-item">
                      <label className="filter-label">Category</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={pendingCategory}
                          onChange={e => setPendingCategory(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {CategoryArray.map(cat => (
                            <MenuItem key={cat.value} value={cat.value} className="dropdown-option">
                              {cat.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )} */}

                  {/* Subject Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Subject</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={pendingSelectedSubject}
                        onChange={e => {
                          setPendingSelectedSubject(e.target.value);
                          // Reset tags when subject changes
                          if (e.target.value !== pendingSelectedSubject) {
                            setPendingSelectedTags([]);
                          }
                        }}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                              maxHeight: 200,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value="All" className="dropdown-option">
                          All
                        </MenuItem>
                        {subjectList.map(subject => (
                          <MenuItem
                            key={subject.id}
                            value={subject.title}
                            className="dropdown-option"
                          >
                            {formatText(subject.title)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Session Type Filter */}
                  {/* <Box className="filter-item">
                    <label className="filter-label">Session Type</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={pendingSessionType}
                        onChange={e => setPendingSessionType(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {SessionTypeArray.map(item => (
                          <MenuItem key={item.value} value={item.value} className="dropdown-option">
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box> */}

                  {/* Tag Filter - Multi-select with radio buttons */}
                  {pendingSelectedSubject !== 'All' && (
                    <Box className="filter-item">
                      <label className="filter-label">
                        Tag{' '}
                        {pendingSelectedTags.length > 0 &&
                          `(${pendingSelectedTags.length} selected)`}
                      </label>
                      <Box
                        className="filter-input"
                        onClick={handleTagDropdownClick}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: pendingSelectedTags.length === 0 ? '#949DA6' : '#14171f',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getPendingTagDisplayText()}
                        </Typography>
                        <KeyboardArrowDownIcon sx={{ color: '#949DA6' }} />
                      </Box>

                      {/* Custom Tag Dropdown */}
                      <Popper
                        open={tagDropdownOpen}
                        anchorEl={tagAnchorEl}
                        placement="bottom-start"
                        modifiers={[
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 2],
                            },
                          },
                          {
                            name: 'preventOverflow',
                            options: {
                              altAxis: true,
                              boundary: document.body,
                            },
                          },
                        ]}
                        style={{
                          zIndex: 1500,
                          width: tagAnchorEl?.offsetWidth,
                          marginTop: '2px',
                        }}
                      >
                        <Paper
                          className="tag-dropdown-paper"
                          onScroll={handleTagListScroll}
                          elevation={3}
                          sx={{
                            maxHeight: 200,
                            overflow: 'auto',
                            border: '1px solid #D5DEE3',
                            borderRadius: '8px',
                          }}
                        >
                          <List className="tag-dropdown-list">
                            {pendingTagList.map(tag => (
                              <ListItem
                                key={tag.id}
                                dense
                                disableGutters
                                disablePadding
                                onClick={() => handleTagSelection(tag.id)}
                                sx={{
                                  padding: '8px 16px',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: 'rgba(10, 94, 243, 0.08)',
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Radio
                                    checked={pendingSelectedTags.includes(tag.id)}
                                    size="small"
                                    disableRipple
                                    sx={{
                                      color: '#949DA6',
                                      '&.Mui-checked': {
                                        color: '#0A5EF3',
                                      },
                                    }}
                                    onChange={() => {}} // Keep React happy with a controlled component
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={formatText(tag.title)}
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '13px',
                                      fontFamily: 'Poppins',
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                            {pendingTagLoading && (
                              <ListItem disabled>
                                <ListItemText primary="Loading..." />
                              </ListItem>
                            )}
                          </List>
                        </Paper>
                      </Popper>
                    </Box>
                  )}

                  {/* Status Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Status</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={pendingStatusFilter}
                        onChange={e => setPendingStatusFilter(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {StatusArray.map(status => (
                          <MenuItem
                            key={status.value}
                            value={status.value}
                            className="dropdown-option"
                          >
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Rating Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Rating</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={pendingRatingFilter}
                        onChange={e => setPendingRatingFilter(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {RatingArray.map(item => (
                          <MenuItem key={item.value} value={item.value} className="dropdown-option">
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Experience Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Years of Experience</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={pendingExperience}
                        onChange={e => setPendingExperience(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {ExperienceArray.map(item => (
                          <MenuItem key={item.value} value={item.value} className="dropdown-option">
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Language Filter with Search */}
                  <Box className="filter-item">
                    <label className="filter-label">Teaching Language</label>
                    <Autocomplete
                      value={
                        pendingLanguage === 'All'
                          ? null
                          : languageList.find(l => l.name === pendingLanguage) || null
                      }
                      onChange={(event, newValue) => {
                        setPendingLanguage(newValue ? newValue.name : 'All');
                      }}
                      inputValue={languageSearch}
                      onInputChange={(event, newInputValue) => {
                        setLanguageSearch(newInputValue);
                      }}
                      options={languageList}
                      getOptionLabel={option => option.name || ''}
                      PopperComponent={props => (
                        <Popper
                          {...props}
                          className="custom-autocomplete-popper"
                          style={{
                            ...props.style,
                            zIndex: 1500,
                            width: props.anchorEl ? props.anchorEl.offsetWidth : 'auto',
                          }}
                          modifiers={[
                            {
                              name: 'flip',
                              enabled: false,
                            },
                            {
                              name: 'preventOverflow',
                              enabled: false,
                            },
                          ]}
                        />
                      )}
                      ListboxProps={{
                        style: {
                          maxHeight: 200,
                          padding: 0,
                        },
                        className: 'custom-autocomplete-listbox',
                      }}
                      PaperComponent={props => (
                        <Paper {...props} className="custom-autocomplete-paper" elevation={3} />
                      )}
                      renderOption={(props, option) => (
                        <MenuItem {...props} key={option.id} className="dropdown-option">
                          {option.name}
                        </MenuItem>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder="Search languages..."
                          InputProps={{
                            ...params.InputProps,
                            className: 'filter-input',
                            endAdornment: (
                              <InputAdornment position="end">
                                <KeyboardArrowDownIcon sx={{ color: '#949DA6' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: 'none',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* Country Filter with Search */}
                  <Box className="filter-item">
                    <label className="filter-label">Country</label>
                    <Autocomplete
                      value={
                        pendingCountry === 'All'
                          ? null
                          : countryList.find(c => c.name === pendingCountry) || null
                      }
                      onChange={(event, newValue) => {
                        setPendingCountry(newValue ? newValue.name : 'All');
                        // Reset city when country changes
                        if (newValue?.name !== pendingCountry) {
                          setPendingCity('All');
                        }
                      }}
                      inputValue={countrySearch}
                      onInputChange={(event, newInputValue) => {
                        setCountrySearch(newInputValue);
                      }}
                      options={countryList}
                      getOptionLabel={option => option.name || ''}
                      PopperComponent={props => (
                        <Popper
                          {...props}
                          className="custom-autocomplete-popper"
                          style={{
                            ...props.style,
                            zIndex: 1500,
                            width: props.anchorEl ? props.anchorEl.offsetWidth : 'auto',
                          }}
                          modifiers={[
                            {
                              name: 'flip',
                              enabled: false,
                            },
                            {
                              name: 'preventOverflow',
                              enabled: false,
                            },
                          ]}
                        />
                      )}
                      ListboxProps={{
                        style: {
                          maxHeight: 200,
                          padding: 0,
                        },
                        className: 'custom-autocomplete-listbox',
                      }}
                      PaperComponent={props => (
                        <Paper {...props} className="custom-autocomplete-paper" elevation={3} />
                      )}
                      renderOption={(props, option) => (
                        <MenuItem {...props} key={option.id} className="dropdown-option">
                          {option.name}
                        </MenuItem>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder="Search countries..."
                          InputProps={{
                            ...params.InputProps,
                            className: 'filter-input',
                            endAdornment: (
                              <InputAdornment position="end">
                                <KeyboardArrowDownIcon sx={{ color: '#949DA6' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: 'none',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* City Filter with Search - Only show when country is selected */}
                  {/* {pendingCountry !== 'All' && (
                    <Box className="filter-item">
                      <label className="filter-label">City</label>
                      <Autocomplete
                        value={
                          pendingCity === 'All'
                            ? null
                            : cityList.find(c => c.name === pendingCity) || null
                        }
                        onChange={(event, newValue) => {
                          setPendingCity(newValue ? newValue.name : 'All');
                        }}
                        inputValue={citySearch}
                        onInputChange={(event, newInputValue) => {
                          setCitySearch(newInputValue);
                        }}
                        options={cityList}
                        getOptionLabel={option => option.name || ''}
                        PopperComponent={props => (
                          <Popper
                            {...props}
                            className="custom-autocomplete-popper"
                            style={{
                              ...props.style,
                              zIndex: 1500,
                              width: props.anchorEl ? props.anchorEl.offsetWidth : 'auto',
                            }}
                            modifiers={[
                              {
                                name: 'flip',
                                enabled: false,
                              },
                              {
                                name: 'preventOverflow',
                                enabled: false,
                              },
                            ]}
                          />
                        )}
                        ListboxProps={{
                          style: {
                            maxHeight: 200,
                            padding: 0,
                          },
                          className: 'custom-autocomplete-listbox',
                        }}
                        PaperComponent={props => (
                          <Paper {...props} className="custom-autocomplete-paper" elevation={3} />
                        )}
                        renderOption={(props, option) => (
                          <MenuItem {...props} key={option.id} className="dropdown-option">
                            {option.name}
                          </MenuItem>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder="Search cities..."
                            InputProps={{
                              ...params.InputProps,
                              className: 'filter-input',
                              endAdornment: (
                                <InputAdornment position="end">
                                  <KeyboardArrowDownIcon sx={{ color: '#949DA6' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: 'none',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  )} */}

                  {/* Gender Filter */}
                  <Box className="filter-item">
                    <label className="filter-label">Gender</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={pendingGender}
                        onChange={e => setPendingGender(e.target.value)}
                        displayEmpty
                        className="filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              zIndex: 1500,
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {GenderArray.map(item => (
                          <MenuItem key={item.value} value={item.value} className="dropdown-option">
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Coupon Code Filter */}
                  {canViewCoupons && (
                    <Box className="filter-item">
                      <label className="filter-label">Coupon Code</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={pendingCouponCode}
                          onChange={e => setPendingCouponCode(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="All" className="dropdown-option">
                            All
                          </MenuItem>
                          {couponList.length === 0 && couponLoading ? (
                            <MenuItem disabled className="dropdown-option">
                              Loading coupons...
                            </MenuItem>
                          ) : (
                            couponList.map(coupon => (
                              <MenuItem
                                key={coupon.id}
                                value={coupon.code}
                                className="dropdown-option"
                              >
                                {coupon.code}
                              </MenuItem>
                            ))
                          )}
                          {hasMoreCoupons && (
                            <MenuItem
                              onClick={loadMoreCoupons}
                              disabled={couponLoading}
                              sx={{
                                justifyContent: 'center',
                                fontStyle: 'italic',
                                color: '#666',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              {couponLoading ? 'Loading...' : 'Load More'}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>

                {/* Revenue Slider - Outside grid, full width */}
                {/* <Box className="filter-revenue-container">
                  <label className="filter-label">Revenue Range</label>
                  <Box className="filter-revenue-wrapper">
                    <Slider
                      value={pendingRevenue}
                      onChange={handleRevenueChange}
                      valueLabelDisplay="off"
                      valueLabelFormat={value => `$${value}`}
                      getAriaValueText={valuetext}
                      min={0}
                      max={10000}
                      step={10}
                      sx={{
                        color: '#0A5EF3',
                        height: 8,
                        '& .MuiSlider-thumb': {
                          height: 24,
                          width: 24,
                          backgroundColor: '#fff',
                          border: '2px solid #0A5EF3',
                          '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                            boxShadow: '0 0 0 8px rgba(10, 94, 243, 0.16)',
                          },
                        },
                        '& .MuiSlider-valueLabel': {
                          backgroundColor: '#0A5EF3',
                          fontFamily: 'Poppins',
                          fontSize: '12px',
                          fontWeight: 500,
                        },
                        '& .MuiSlider-rail': {
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#E5E7EB',
                        },
                        '& .MuiSlider-track': {
                          height: 8,
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Box className="filter-revenue-values">
                      <Typography className="filter-revenue-value">
                        QAR {pendingRevenue[0]}
                      </Typography>
                      <Typography className="filter-revenue-value">
                        QAR {pendingRevenue[1]}
                      </Typography>
                    </Box>
                  </Box>
                </Box> */}

                {/* Total Spent Filter */}
                {/* <Box className="filter-item">
                  <label className="filter-label">Total Spent</label>
                  <Box className="filter-revenue-wrapper">
                    <Slider
                      value={pendingTotalSpent}
                      onChange={handlePendingTotalSpentChange}
                      valueLabelDisplay="off"
                      valueLabelFormat={valuetext}
                      min={0}
                      max={10000}
                      step={10}
                      sx={{
                        color: '#0A5EF3',
                        height: 8,
                        '& .MuiSlider-thumb': {
                          height: 24,
                          width: 24,
                          backgroundColor: '#fff',
                          border: '2px solid #0A5EF3',
                          '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                            boxShadow: '0 0 0 8px rgba(10, 94, 243, 0.16)',
                          },
                        },
                        '& .MuiSlider-valueLabel': {
                          backgroundColor: '#0A5EF3',
                          fontFamily: 'Poppins',
                          fontSize: '12px',
                          fontWeight: 500,
                        },
                        '& .MuiSlider-rail': {
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#E5E7EB',
                        },
                        '& .MuiSlider-track': {
                          height: 8,
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Box className="filter-revenue-values">
                      <Typography className="filter-revenue-value">
                        $ {pendingTotalSpent[0]}
                      </Typography>
                      <Typography className="filter-revenue-value">
                        $ {pendingTotalSpent[1]}
                      </Typography>
                    </Box>
                  </Box>
                </Box> */}

                {/* Total Sessions Filter */}
                {/* <Box className="filter-item">
                  <label className="filter-label">Total Sessions</label>
                  <Box className="filter-revenue-wrapper">
                    <Slider
                      value={pendingTotalSessions}
                      onChange={handlePendingTotalSessionsChange}
                      valueLabelDisplay="off"
                      valueLabelFormat={valuetext}
                      min={0}
                      max={1000}
                      step={1}
                      sx={{
                        color: '#0A5EF3',
                        height: 8,
                        '& .MuiSlider-thumb': {
                          height: 24,
                          width: 24,
                          backgroundColor: '#fff',
                          border: '2px solid #0A5EF3',
                          '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                            boxShadow: '0 0 0 8px rgba(10, 94, 243, 0.16)',
                          },
                        },
                        '& .MuiSlider-valueLabel': {
                          backgroundColor: '#0A5EF3',
                          fontFamily: 'Poppins',
                          fontSize: '12px',
                          fontWeight: 500,
                        },
                        '& .MuiSlider-rail': {
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#E5E7EB',
                        },
                        '& .MuiSlider-track': {
                          height: 8,
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Box className="filter-revenue-values">
                      <Typography className="filter-revenue-value">
                        {pendingTotalSessions[0]}
                      </Typography>
                      <Typography className="filter-revenue-value">
                        {pendingTotalSessions[1]}
                      </Typography>
                    </Box>
                  </Box>
                </Box> */}
              </Box>

              {/* Buttons (Sticky Footer) */}
              <Box className="filter-buttons-container">
                <Button variant="outlined" onClick={handleClearAll} className="filter-clear-button">
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  className="filter-apply-button"
                >
                  Apply
                </Button>
              </Box>
            </Popover>

            <Box className="teacher-table-container">
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    background: '#FFF',
                  }}
                >
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="teacher type tabs">
                    <Tab
                      label="Academic"
                      aria-label="academic tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(0)}
                    />
                    <Tab
                      label="Creative Arts"
                      aria-label="creative arts tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(1)}
                    />
                  </Tabs>
                </Box>

                {/* Active Filters Display */}
                {getActiveFilters().length > 0 && (
                  <Box className="active-filters-container">
                    {getActiveFilters().map((filter, index) => (
                      <Box key={index} className="filter-card">
                        <Typography className="filter-card-text">{filter.label}</Typography>
                        <CloseIcon
                          className="filter-card-close"
                          onClick={() => handleRemoveFilter(filter.type)}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Sort By - matches Students */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '8px',
                    px: 2,
                    py: 1,
                    background: 'transparent',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: '#14171F',
                    }}
                  >
                    Sort by
                  </Typography>
                  <Select
                    value={sortBy}
                    onChange={e => {
                      setSortBy(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                    sx={{
                      height: '30px',
                      minWidth: '160px',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '6px 8px 4px 8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                        borderRadius: '8px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      background: '#FFF',
                    }}
                    renderValue={selected => {
                      if (!selected) return <span style={{ color: '#949DA6' }}>Select</span>;
                      if (selected === 'total_earnings') return 'Total earnings';
                      if (selected === 'total_sessions') return 'Total sessions';
                      if (selected === 'years_of_experience') return 'Years of experience';
                      return selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="total_earnings">Total earnings</MenuItem>
                    <MenuItem value="total_sessions">Total sessions</MenuItem>
                    <MenuItem value="years_of_experience">Years of experience</MenuItem>
                  </Select>
                  <Select
                    value={sortOrder}
                    onChange={e => {
                      setSortOrder(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                    disabled={!sortBy}
                    sx={{
                      height: '30px',
                      minWidth: '120px',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '6px 8px 4px 8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                        borderRadius: '8px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D5DEE3',
                      },
                      background: '#FFF',
                    }}
                    renderValue={selected => {
                      if (!selected) return <span style={{ color: '#949DA6' }}>Order</span>;
                      return selected === 'desc' ? 'Desc' : 'Asc';
                    }}
                  >
                    <MenuItem value="asc">Asc</MenuItem>
                    <MenuItem value="desc">Desc</MenuItem>
                  </Select>
                </Box>

                {loading || !hasReceivedDataRef.current ? (
                  <TableSkeleton />
                ) : (
                  <>
                    <CustomTabPanel value={tabValue} index={0}>
                      <TeacherTable
                        searchTerm={searchTerm}
                        category={category}
                        teacherRequestData={getFilteredData()}
                        value={statusFilter}
                        selectedSubject={selectedSubject}
                        tag={selectedTags.join(', ')}
                        experience={experience}
                        language={language}
                        city={city}
                        country={country}
                        gender={gender}
                        revenue={revenue}
                        selectedIds={selectedIds}
                        onSelectOne={handleSelectOne}
                        onSelectAll={handleSelectAllVisible}
                      />
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                      <TeacherTable
                        searchTerm={searchTerm}
                        category={category}
                        teacherRequestData={getFilteredData()}
                        value={statusFilter}
                        selectedSubject={selectedSubject}
                        tag={selectedTags.join(', ')}
                        experience={experience}
                        language={language}
                        city={city}
                        country={country}
                        gender={gender}
                        revenue={revenue}
                        selectedIds={selectedIds}
                        onSelectOne={handleSelectOne}
                        onSelectAll={handleSelectAllVisible}
                        tableType="creative-arts"
                      />
                    </CustomTabPanel>
                  </>
                )}

                {/* Pagination UI */}
                {!loading && (
                  <Box className="teacher-pagination-container">
                    <Box className="teacher-pagination-info">
                      <Typography className="teacher-pagination-text">Showing</Typography>
                      <Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        className="teacher-pagination-select"
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={30}>30</MenuItem>
                      </Select>
                      <Typography className="teacher-pagination-text">
                        of {totalCount} Instructors
                      </Typography>
                    </Box>
                    <nav className="teacher-pagination-nav">
                      <ul className="teacher-pagination-list">
                        {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                          let children = null;

                          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                            children = <span className="teacher-pagination-ellipsis">…</span>;
                          } else if (type === 'page') {
                            children = (
                              <button
                                type="button"
                                className={`teacher-pagination-button ${selected ? 'selected' : ''}`}
                                {...item}
                              >
                                {itemPage}
                              </button>
                            );
                          } else {
                            children = (
                              <button type="button" className="teacher-pagination-button" {...item}>
                                {formatText(type)}
                              </button>
                            );
                          }

                          return (
                            <li key={index} className="teacher-pagination-item">
                              {children}
                            </li>
                          );
                        })}
                      </ul>
                    </nav>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Teacher;
